import Store from '@ember-data/store';
import Service, { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { type SelectOption } from 'donor-elf-web/lib/form-component-utils';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import AdminFund from 'donor-elf-web/models/admin/fund';
import AdminFundHostIdentity from 'donor-elf-web/models/admin/fund-host-identity';
import AdminHostFundModel from 'donor-elf-web/models/admin/host-fund';
import fuzzysort from 'fuzzysort';

export default class FundMappingsService extends Service {
  @service declare store: Store;

  @tracked funds: AdminFund[] = [];
  @tracked hostIdentities: AdminFundHostIdentity[] = [];
  @tracked hostFunds: AdminHostFundModel[] = [];

  databaseHostFundOptions: { [database: string]: SelectOption[] } = {};

  @cached
  get fundMappings(): FundMapping[] {
    let { hostIdentitiesByFundId } = this;
    return this.funds.map((fund) => {
      return new FundMapping({
        fund: fund,
        hostIdentities: hostIdentitiesByFundId[fund.id],
      });
    });
  }

  get hostIdentitiesByFundId(): { [index: string]: AdminFundHostIdentity[] } {
    let byId: any = {};
    this.hostIdentities.forEach((hostIdentity) => {
      let fundId = hostIdentity.identifiableId;
      byId[fundId] = byId[fundId] || [];
      byId[fundId].push(hostIdentity);
    });
    return byId;
  }

  getHostFund(args: {
    database?: string;
    hostId: string;
  }): AdminHostFundModel | undefined {
    let database: string = isEmpty(args.database) ? 'null' : args.database!;
    return this.hostFundsByDatabaseAndHostId[database]?.[args.hostId];
  }

  // indexed by database then host id
  // for host funds with a blank database, then 'null' is used for its key
  @cached
  get hostFundsByDatabaseAndHostId() {
    let data: { [database: string]: { [hostId: string]: AdminHostFundModel } } =
      {};
    this.hostFunds.forEach((x) => {
      let database = x.databaseId!;
      if (isEmpty(database)) {
        database = 'null';
      }
      if (data[database] == null) {
        data[database] = {};
      }
      data[database][x.hostId] = x;
    });
    return data;
  }

  @cached
  get availableDatabaseOptions(): SelectOption[] {
    let databaseAndLabels: any = {};
    this.hostIdentities.forEach((x) => {
      let database = x.database || '';
      if (databaseAndLabels[database] == null) {
        databaseAndLabels[database] = HostDatabaseLabels[database] || database;
      }
    });
    this.hostFunds.forEach((x) => {
      let database = x.databaseId || '';
      if (databaseAndLabels[database] == null) {
        databaseAndLabels[database] = HostDatabaseLabels[database] || database;
      }
    });

    return sortByProperties(
      Object.keys(databaseAndLabels).map((database) => {
        return {
          val: database,
          label: databaseAndLabels[database],
        };
      }),
      ['label'],
    );
  }

  _loadPromise?: Promise<any>;
  load(): Promise<void> {
    if (this._loadPromise) {
      return this._loadPromise;
    }

    return (this._loadPromise = queryWithPages<AdminFund>({
      store: this.store,
      modelName: 'admin/fund',
      pageSize: 500,
    }).then((funds) => {
      this.funds = funds;
      return queryWithPages<AdminFundHostIdentity>({
        store: this.store,
        modelName: 'admin/fund-host-identity',
        pageSize: 500,
      }).then((hostIdentities) => {
        this.hostIdentities = hostIdentities;
        return queryWithPages<AdminHostFundModel>({
          store: this.store,
          modelName: 'admin/host-fund',
          pageSize: 1000,
        }).then((hostFunds) => {
          this.hostFunds = hostFunds;
          this.createDatabaseHostFundOptions();
        });
      });
    }));
  }

  createDatabaseHostFundOptions() {
    this.databaseHostFundOptions = {};
    let databases = this.availableDatabaseOptions.map((x) => x.val);
    let { hostFundsByDatabaseAndHostId } = this;
    databases.forEach((database) => {
      let databaseHostFundsById = hostFundsByDatabaseAndHostId[database];
      if (databaseHostFundsById) {
        this.databaseHostFundOptions[database] = sortByProperties(
          Object.keys(databaseHostFundsById).map((hostId) => {
            let hostFund = databaseHostFundsById[hostId];
            return {
              val: hostFund.hostId,
              label: hostFund.name,
            };
          }),
          ['label'],
        );
      }
    });
  }

  updateMappingsForFund(
    fund: AdminFund,
    fundHostIdentities: AdminFundHostIdentity[],
  ) {
    this.funds = [fund, ...this.funds.filter((x) => x.id !== fund.id)];
    this.hostIdentities = [
      ...fundHostIdentities,
      ...this.hostIdentities.filter((x) => x.identifiableId !== fund.id),
    ];
  }
}

export class FundMapping {
  constructor(args: {
    fund: AdminFund;
    hostIdentities?: AdminFundHostIdentity[];
  }) {
    this.fund = args.fund;
    this.hostIdentities = args.hostIdentities || [];
    this.fuzzySortPrepared = fuzzysort.prepare(
      `${this.fund.name} ${this.hostIdentities.map((x) => x.hostId).join(' ')}`,
    );
  }

  fund: AdminFund;
  fuzzySortPrepared: Fuzzysort.Prepared;
  @tracked hostIdentities: AdminFundHostIdentity[];
}

export let HostDatabaseLabels: { [index: string]: string } = {
  aplos: 'Aplos',
  aplos_fund: 'Aplos Fund',
  breeze: 'Breeze',
  ccb: 'Church Community Builder',
  csv: 'CSV File',
  dp: 'Donor Perfect',
  etap: 'eTapestry',
  f1: 'Fellowship 1',
  FE: 'Financial Edge',
  GP: 'Great Plains',
  kf: 'Kindful',
  pc_c: 'Power Church Contribution',
  pc_f: 'Power Church Fund',
  QuickBooks: 'QuickBooks',
  RE: 'Raisers Edge',
  sage: 'Sage',
  SF: 'SalesForce',
  sky: 'Raisers Edge NXT',
  sky_fe: 'Financial Edge NXT',
  ss: 'SiteStacker',
  virt: 'Virtuous',
  xero: 'Xero',
};
